import { Component, Watch, Mixins, Inject } from 'vue-property-decorator';
import { mapActions, mapMutations } from 'vuex';
import { State, namespace } from 'vuex-class';
import { format, eachDay, addDays, subDays, isBefore } from 'date-fns';
import { Route } from 'vue-router';
import ExtButton from '@/components/ext/Button.vue';
import { OrderDatepicker } from '@/components/orders';
import ExtLink from '@/components/ext/Link.vue';
import PageError from '../../404.vue';
import ButtonCanRequest from '@/presets/button/can-request';
import TabsMenu from '@/components/parts/tab/menu';
import SliderImages from '@/components/parts/slider/images';
import { TransitionFade } from '@/components/transition';
import CommonMixin from '../common';
import { Breadcrumbs } from '../types';
import CardMixin from './common';
import AuthorizedMixin from '@/mixins/isAuthorized';
import isArrayNotEmpty from '@/tools/isarraynotempty';
import {
  Breadcrumbs as BreadcrumbsLayout,
  Extra as ExtraLayout,
} from '@/components/layout';
import {
  List as ProgramsList,
  Items as ActivitiesItems,
} from '@/components/parts/catalog/list';
import LinkElement from '@/components/parts/catalog/card/components/sidebar/components/LinkElement.vue';
import SidebarElement from '@/components/parts/catalog/card/components/sidebar/components/SidebarElement.vue';
import PageOrganization, {
  NamePositions,
  Props as PageOrganizationProps,
  SlotNames,
} from '@/components/page/organization';
import { ACTION_FETCH, STORE_MODULE_NAME, SEND_ACTIVITY_ORDER } from './const';
import createStoreModule from './store';
import ButtonDeferred from './button-deferred';
import { ACTIVITY_ROUTE } from '../const';
import {
  ActivityExtended,
  State as StateType,
  Statuses,
  Url,
  Children,
  Shedule,
  Links,
  ITimePeriodsByDates,
  ITimePeriodsByDatesItem,
} from './types';
import ActivityOrderProcess from '@/components/ordersProcessForms/activity/ActivityOrderProcess.vue';

const { ERROR } = Statuses;
const Organization = namespace(STORE_MODULE_NAME);
const actions = mapActions(STORE_MODULE_NAME, [
  ACTION_FETCH,
  SEND_ACTIVITY_ORDER,
]);
const fetch = actions[ACTION_FETCH];
const send = actions[SEND_ACTIVITY_ORDER];

const isError = (value: any) => value === ERROR;

@Component({
  components: {
    BreadcrumbsLayout,
    ButtonCanRequest,
    ExtButton,
    OrderDatepicker,
    ExtraLayout,
    PageError,
    PageOrganization,
    ProgramsList,
    SidebarElement,
    LinkElement,
    SliderImages,
    TabsMenu,
    TransitionFade,
    ExtLink,
    ButtonDeferred,
  },
  metaInfo() {
    const { title } = this as PageActivityCard;
    return { title };
  },
  storeName: STORE_MODULE_NAME,
  storeRegister: createStoreModule,
})
export default class PageActivityCard extends Mixins(
  CardMixin,
  CommonMixin,
  AuthorizedMixin
) {
  @Inject() $eventVisitHistory: any;
  // Bindings
  @Organization.State
  status: StateType['status'];

  @Organization.State
  srvErrorMsg: String;

  @Organization.State
  activities: StateType['activities'];

  @Organization.Getter
  full: ActivityExtended;

  @Organization.State
  children: Children;

  @Organization.State
  shedule: Shedule;

  @Organization.State
  timePeriodsByDates: ITimePeriodsByDates;

  @State municipalities: Array<any>;

  // Readonly
  readonly bestProgramsTitle: string = 'Похожие мероприятия';
  readonly slotNames = SlotNames;

  dateOrder: Date = new Date();
  selectedTimeItem: ITimePeriodsByDatesItem | null = null;

  // Computed
  get breadcrumbsExtended(): Breadcrumbs {
    const { activity } = this;
    const result = [...this.breadcrumbs];
    if (activity) result.push({ text: activity.name });
    return result;
  }

  get partnerLink() {
    const date = this.routeQuery ? new Date(this.routeQuery) : null;
    return {
      name: 'activities-calendar',
      query: {
        organizator: this.full.partnerId,
        year: date ? date.getFullYear() : undefined,
        month: date ? date.getMonth() + 1 : undefined,
      },
    };
  }

  get hasError(): boolean {
    return isError(this.status);
  }

  get hasSending(): boolean {
    return this.status === Statuses.SENDING;
  }

  get hasSent(): boolean {
    return this.status === Statuses.SENT;
  }

  get hasPrepared() {
    const { activitiesPrepared } = this;
    return activitiesPrepared && activitiesPrepared.length > 0;
  }

  get hasShedule(): Array<any> {
    return this.shedule;
  }

  get periodStart(): number {
    return parseInt(this.activity.periodStart, 10);
  }

  get hasSlides(): boolean {
    return !!this.slides.length;
  }

  get menu() {
    const { press, results, id } = this.activity;
    const links: Array<Links> = [
      { title: 'Описание', to: { name: 'activity', params: { id } } },
    ];
    if (press)
      links.push({
        title: 'Пресс-релиз',
        to: { name: 'activity-press', params: { id } },
      });
    if (results)
      links.push({
        title: 'Пост-релиз',
        to: { name: 'activity-result', params: { id } },
      });
    return links;
  }

  get pageBinds(): PageOrganizationProps {
    const { municipality } = this.full;
    const query = { municipality: String(municipality && municipality.id) };
    return {
      name: this.activity.name,
      namePosition: NamePositions.TOP,
      municipalityLink: { query, name: 'activities' },
      allProgramsTitle: 'Все мероприятия организатора',
      /*
      programsLink: {
        name: 'activities',
        query: { ...query, organizer: String(this.activity.id) },
      },
      */
    };
  }

  get orderBinds(): any {
    const {
      activity,
      full,
      hasSending,
      hasSent,
      dateOrder,
      activeDates,
      disabledDates,
      selectedTimeItem,
      srvErrorMsg,
    } = this;

    return {
      title: activity.name,
      address: full.location,
      membersOfActivity: full.members.join(', '),
      timeId: selectedTimeItem ? selectedTimeItem.id : null,
      hasSending,
      hasSent,
      dateOrder,
      activeDates,
      disabledDates,
      srvErrorMsg,
    };
  }

  get activitiesPrepared(): ActivitiesItems {
    const { activities } = this;
    if (!activities) return [];

    return activities.map(({ announce, id, description, imageSrc, name }) => ({
      id,
      code: '',
      note: announce,
      text: description !== null ? description : '',
      image: {
        small: imageSrc || '',
        large: imageSrc || '',
      },
      link: {
        name: ACTIVITY_ROUTE,
        params: { id: String(id) },
      },
      title: name,
    }));
  }

  //Возвращает массив url изображений (обложка + картинки из галереи)
  get slides() {
    const result: Array<Url> = [];
    const { imageSrc, images } = this.activity;
    if (imageSrc) result.push(imageSrc);
    if (images && images.length) {
      const urls = images.map((item) => item.url);
      return result.concat(urls);
    }
    return result;
  }

  get title() {
    return this.activity.name;
  }

  get isOrderPosible() {
    const {
      activity: { isLocked },
      isBeforeDayStart,
      hasShedule,
      closeBeforeEndDate,
    } = this;
    return (
      !isLocked &&
      isBeforeDayStart &&
      closeBeforeEndDate &&
      hasShedule.length > 0
    );
  }

  get activityId() {
    return this.activity.id ? this.activity.id.toString() : '';
  }

  get metaRoute() {
    return this.$route.meta.isOrder;
  }

  get closeBeforeEndDate() {
    const { periodStart, orderDayEnd } = this;
    return isBefore(new Date(), subDays(orderDayEnd, periodStart));
  }

  get isBeforeDayStart(): boolean {
    const { dayStart, orderDayEnd } = this;
    return isBefore(dayStart, orderDayEnd);
  }

  get dayStart(): Date {
    const { hasShedule } = this;
    if (!hasShedule.length) return new Date();
    return new Date(hasShedule[0]);
  }

  get orderDayStart(): Date {
    const { isBeforeDayStart, dayStart, orderDayEnd } = this;
    return isBeforeDayStart ? dayStart : subDays(orderDayEnd, 1);
  }

  get orderDayEnd(): Date {
    const { hasShedule } = this;
    if (!hasShedule.length) return addDays(new Date(), 1);
    return addDays(new Date(hasShedule[hasShedule.length - 1]), 1);
  }

  get orderPeriod(): Array<any> {
    const { orderDayStart, orderDayEnd } = this;
    return eachDay(orderDayStart, orderDayEnd);
  }

  get activeDates() {
    const { hasShedule } = this;
    if (!hasShedule.length) return {};
    return {
      dates: hasShedule.map((date: any) => new Date(date)),
      includeDisabled: true,
    };
  }

  get disabledDates() {
    const { hasShedule, orderDayStart, orderDayEnd, orderPeriod } = this;
    if (!hasShedule.length) return {};
    const to = orderDayStart;
    const from = orderDayEnd;
    const dates = orderPeriod.reduce((result: Array<any>, section: any) => {
      if (!hasShedule.includes(format(section, 'YYYY-MM-DD'))) {
        result.push(section);
      }
      return result;
    }, [] as Array<any>);
    return { to, from, dates };
  }

  get routeQuery(): any {
    const { date = '' } = this.$route.query;
    return date;
  }

  get isCorrectRouteQuery(): boolean {
    const { routeQuery } = this;
    const re = /[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|1[0-9]|2[0-9]|3[01])/;
    return !!routeQuery.length && routeQuery.match(re) !== null;
  }

  get dateFromRoute(): Date {
    const { routeQuery, isCorrectRouteQuery } = this;
    return isCorrectRouteQuery ? new Date(routeQuery) : new Date();
  }

  get calendarDate() {
    const { dateFromRoute, orderDayStart } = this;
    return isBefore(dateFromRoute, orderDayStart)
      ? orderDayStart
      : dateFromRoute;
  }

  get colorMenuSection() {
    const { colorMenuSection } = this.$store.state.regionProfile;
    return colorMenuSection ? `#${colorMenuSection}` : '#8164a6';
  }

  created() {
    this.dateOrder = this.calendarDate;
  }

  mounted() {
    this.saveVisitToHistory();
  }

  saveVisitToHistory() {
    this.$nextTick(() => {
      const { $eventVisitHistory } = this;
      if (!$eventVisitHistory) {
        return;
      }

      const { announce, id, imageSrc = {}, name } = this.activity;

      $eventVisitHistory.save({
        id,
        code: '',
        image: {
          small: imageSrc,
        },
        price: null,
        is_locked: false,
        title: name,
        note: announce,
        isActivity: true,
      });
    });
  }

  backToActivity() {
    this.$router.push({
      name: 'activity',
      params: { id: this.activityId },
    });
  }

  openForm() {
    if (this.isAuthorized) {
      this.$ui.$modal.show('activityOrderProcess', {
        settings: {
          size: {
            width: '710px',
            minHeight: '500px',
            height: 'auto',
          },
          title: 'Запись на мероприятие',
        },
        bind: this,
        slots: {
          body: {
            component: ActivityOrderProcess,
            propsData: {
              orderBinds: this.orderBinds,
              /// isError: this.isError,
            },
          },
        },
      });
    } else {
      this.$router.push({
        hash: this.$util.getRouteHashAllowAuthMethod(
          this.$store.state.apiModules
        ),
      });
    }
  }

  dateChanged(value: Date) {
    this.dateOrder = value;
  }

  // AsyncData
  async asyncData({ store, route }: any) {
    await fetch.call({ $store: store }, route.params);
  }

  async onSubmit(selectedTimeItem: ITimePeriodsByDatesItem, eduLvl: any) {
    const { activityId, dateOrder } = this;
    const userId = this.$store.getters['user/userId'];
    const data = {
      activity_id: activityId,
      activity_date: selectedTimeItem.startDate,
      students: [{ id: userId, edu_level: eduLvl ? eduLvl : undefined }],
    };
    const payload = {
      data: data,
    };

    await send.call(this, payload);
  }
}



// WEBPACK FOOTER //
// ./src/pages/activities/card/script.ts